import { useQuery } from '@tanstack/vue-query';
import { toValue, type MaybeRefOrGetter } from 'vue';
import { computed } from 'vue';
import {
  getEventContractMarketOpenInterest,
  getAggOpenInterest,
  getEventContractOptionOpenInterest,
  getEventContractBidAsk,
  getFlexbestBidAsk,
} from './mdfarm.api';
import type {
  MdfarmEventContractMarketOpenInterestParams,
  MdfarmAggOpenInterestParams,
  MdfarmEventContractBidAskResponse,
} from '@/types/mdfarm.types';
import { useUserState } from '@/stores/useUserState';

/**
 * Query hook for event contract market open interest
 */
export const useEventContractMarketOpenInterest = (
  params: MaybeRefOrGetter<MdfarmEventContractMarketOpenInterestParams>,
) => {
  const userState = useUserState();

  return useQuery({
    queryKey: computed(() => {
      const paramsValue = toValue(params);
      return [
        'mdfarm',
        'event-contract',
        'market-open-interest',
        { ids: paramsValue.ids || [], usePublicApi: userState.usePublicApi },
      ];
    }),
    queryFn: () => getEventContractMarketOpenInterest(toValue(params), userState.usePublicApi),
    staleTime: 30 * 1000, // 30 seconds - open interest data updates frequently
    refetchInterval: 60 * 1000, // 1 minute
    refetchIntervalInBackground: false,
    retry: false,
    enabled: () => {
      const paramsValue = toValue(params);
      return paramsValue.ids && paramsValue.ids.length > 0;
    },
  });
};

/**
 * Query hook for aggregated open interest across venues.
 * Pass YES-side conid@exchange pairs for a strike row.
 */
export const useAggOpenInterest = (params: MaybeRefOrGetter<MdfarmAggOpenInterestParams>) => {
  const userState = useUserState();

  return useQuery({
    queryKey: computed(() => {
      const paramsValue = toValue(params);
      return [
        'mdfarm',
        'event-contract',
        'agg-open-interest',
        { conidExPairs: paramsValue.conidExPairs, usePublicApi: userState.usePublicApi },
      ];
    }),
    queryFn: () => getAggOpenInterest(toValue(params), userState.usePublicApi),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    retry: false,
    enabled: () => {
      const paramsValue = toValue(params);
      return paramsValue.conidExPairs && paramsValue.conidExPairs.length > 0;
    },
  });
};

/**
 * Query hook for event contract open interest
 */
export const useOptionOpenInterest = (
  conid: MaybeRefOrGetter<number | undefined>,
  exchange: MaybeRefOrGetter<string | undefined>,
) => {
  const userState = useUserState();
  return useQuery({
    queryKey: computed(() => [
      'mdfarm',
      'event-contract',
      'open-interest',
      { conid: toValue(conid), exchange: toValue(exchange), usePublicApi: userState.usePublicApi },
    ]),
    queryFn: () => getEventContractOptionOpenInterest(toValue(conid)!, toValue(exchange)!, userState.usePublicApi),
    retry: false,
    staleTime: 30 * 1000,
    enabled: () => !!toValue(conid) && !!toValue(exchange),
  });
};

/**
 * Shared query configuration for event contract bid-ask polling
 * Single source of truth to avoid duplication between factory and hook
 */
export const EVENT_CONTRACT_BID_ASK_QUERY_CONFIG = {
  staleTime: 60_000,
  refetchInterval: 60_000,
  refetchIntervalInBackground: false,
  gcTime: 180_000,
  retry: false,
  refetchOnWindowFocus: false,
} as const;

/** Normalize bid/ask: any negative value (e.g. -1 = "no market") becomes null */
export const normalizeBidAsk = (data: MdfarmEventContractBidAskResponse): MdfarmEventContractBidAskResponse => ({
  bid: data.bid !== null && data.bid >= 0 ? data.bid : null,
  ask: data.ask !== null && data.ask >= 0 ? data.ask : null,
  bid_size: data.bid_size ?? null,
  ask_size: data.ask_size ?? null,
});

/**
 * Query options factory for event contract bid-ask data
 * Use with useQueries for parallel fetching of multiple contracts
 */
export const eventContractBidAskQueryOptions = (
  conid: number | undefined,
  exchange: string,
  usePublicApi: boolean,
) => ({
  queryKey: ['mdfarm', 'event-contract', 'bid-ask', { conid, exchange, usePublicApi }] as const,
  queryFn: () => getEventContractBidAsk(conid!, exchange, usePublicApi),
  select: normalizeBidAsk,
  ...EVENT_CONTRACT_BID_ASK_QUERY_CONFIG,
  enabled: !!conid,
});

/**
 * Query options factory for event contract option open interest data
 * Use with useQueries for parallel fetching of multiple contracts
 */
export const eventContractOptionOIQueryOptions = (
  conid: number | undefined,
  exchange: string,
  usePublicApi: boolean,
) => ({
  queryKey: ['mdfarm', 'event-contract', 'open-interest', { conid, exchange, usePublicApi }] as const,
  queryFn: async () => {
    const { getEventContractOptionOpenInterest } = await import('./mdfarm.api');
    return getEventContractOptionOpenInterest(conid!, exchange, usePublicApi);
  },
  staleTime: Infinity,
  retry: false,
  refetchOnWindowFocus: false,
  enabled: !!conid,
});

/**
 * Query hook for event contract bid-ask data (single contract)
 *
 * @param options.isScrolling - When provided, pauses refetchInterval during scroll.
 *   Consumers on the main scroll surface (YesNoBid, IllustratedBidAsk) opt in;
 *   drawer/panel consumers omit it to poll unconditionally.
 * @param options.isLive - When provided and false, pauses refetchInterval (component is
 *   mounted but off-screen). Does not disable the initial fetch — the first mount still
 *   loads data so it's visible when the row scrolls back into view.
 */
export const useGetEventContractBidAsk = (
  conid: MaybeRefOrGetter<number | undefined>,
  exchange: MaybeRefOrGetter<string>,
  options?: { isScrolling?: MaybeRefOrGetter<boolean>; isLive?: MaybeRefOrGetter<boolean> },
) => {
  const userState = useUserState();
  const shouldPausePolling = () => {
    if (options?.isScrolling && toValue(options.isScrolling)) return true;
    if (options?.isLive !== undefined && !toValue(options.isLive)) return true;
    return false;
  };

  return useQuery<MdfarmEventContractBidAskResponse, Error, MdfarmEventContractBidAskResponse>({
    queryKey: computed(() => [
      'mdfarm',
      'event-contract',
      'bid-ask',
      { conid: toValue(conid), exchange: toValue(exchange), usePublicApi: userState.usePublicApi },
    ]),
    queryFn: () => getEventContractBidAsk(toValue(conid)!, toValue(exchange), userState.usePublicApi),
    select: normalizeBidAsk,
    staleTime: EVENT_CONTRACT_BID_ASK_QUERY_CONFIG.staleTime,
    retry: EVENT_CONTRACT_BID_ASK_QUERY_CONFIG.retry,
    refetchOnWindowFocus: EVENT_CONTRACT_BID_ASK_QUERY_CONFIG.refetchOnWindowFocus,
    refetchInterval: () => (shouldPausePolling() ? false : EVENT_CONTRACT_BID_ASK_QUERY_CONFIG.refetchInterval),
    enabled: () => !!toValue(conid),
  });
};

/**
 * Query hook for flexbest bid-ask data (best across multiple exchanges)
 *
 * @param conidExPairs - Array of "conid@exchange" strings. Query disabled when undefined or length <= 1.
 * @param options.isScrolling - When provided, pauses refetchInterval during scroll.
 * @param options.isLive - When provided and false, pauses refetchInterval (off-screen). Initial fetch still runs.
 */
export const useGetFlexbestBidAsk = (
  conidExPairs: MaybeRefOrGetter<string[] | undefined>,
  options?: { isScrolling?: MaybeRefOrGetter<boolean>; isLive?: MaybeRefOrGetter<boolean> },
) => {
  const userState = useUserState();
  const shouldPausePolling = () => {
    if (options?.isScrolling && toValue(options.isScrolling)) return true;
    if (options?.isLive !== undefined && !toValue(options.isLive)) return true;
    return false;
  };

  return useQuery<MdfarmEventContractBidAskResponse, Error, MdfarmEventContractBidAskResponse>({
    queryKey: computed(() => [
      'mdfarm',
      'event-contract',
      'flexbest-bid-ask',
      { conidExPairs: toValue(conidExPairs), usePublicApi: userState.usePublicApi },
    ]),
    queryFn: () => getFlexbestBidAsk(toValue(conidExPairs)!, userState.usePublicApi),
    select: normalizeBidAsk,
    staleTime: EVENT_CONTRACT_BID_ASK_QUERY_CONFIG.staleTime,
    gcTime: EVENT_CONTRACT_BID_ASK_QUERY_CONFIG.gcTime,
    retry: EVENT_CONTRACT_BID_ASK_QUERY_CONFIG.retry,
    refetchOnWindowFocus: EVENT_CONTRACT_BID_ASK_QUERY_CONFIG.refetchOnWindowFocus,
    refetchIntervalInBackground: false,
    refetchInterval: () => (shouldPausePolling() ? false : EVENT_CONTRACT_BID_ASK_QUERY_CONFIG.refetchInterval),
    enabled: () => {
      const pairs = toValue(conidExPairs);
      return !!pairs && pairs.length > 1;
    },
  });
};
